import type { PayrollItem } from "features/Payroll/types/payroll.types";
import { payrollItemEmployeeLabel } from "features/Payroll/utils/payrollEmployeeDisplay";
import {
  canPayPayrollItem,
  payrollItemStatusMap,
  PayrollItemStatus,
} from "features/Payroll/constants/payroll.constants";
import { formatCurrency } from "utils/currency";
import { Link } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Table } from "reactstrap";
import StatusBadge from "components/common/StatusBadge";

export interface PayrollRunEmployeesInRunTotals {
  employees: number;
  gross: number;
  net: number;
}


export interface PayrollRunEmployeesInRunProps {
  items: PayrollItem[];
  totals: PayrollRunEmployeesInRunTotals;
  onViewSlip?: (itemId: number) => void;
  onAdjust?: (item: PayrollItem) => void;
  onPay?: (item: PayrollItem) => void;
  onViewReceipt?: (item: PayrollItem) => void;
}

const PayrollRunEmployeesInRun = ({
  items,
  totals,
  onViewSlip,
  onAdjust,
  onPay,
  onViewReceipt,
}: PayrollRunEmployeesInRunProps) => {
  const hasActionsColumn = Boolean(onViewSlip || onAdjust || onPay || onViewReceipt);
  return (
    <Card>
      <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h5 className="card-title">Employees in this run</h5>
          <p className="text-muted small mb-0">
            Gross, net pay, and breakdown of earnings and deductions per person.
          </p>
        </div>
        <span className="badge bg-primary-subtle text-primary fs-12">
          {totals.employees} {totals.employees === 1 ? "employee" : "employees"}
        </span>
      </CardHeader>
      <CardBody>
        {items.length ? (
          <div className="table-responsive table-card">
            <Table hover className="align-middle mb-0">
              <thead className="table-light text-muted">
                <tr>
                  <th scope="col">Employee</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="text-end">
                    Monthly Salary
                  </th>
                  <th scope="col" className="text-end">
                    Gross pay
                  </th>
                  <th scope="col" className="text-end">
                    Net pay
                  </th>
                  <th scope="col">Earnings</th>
                  <th scope="col">Deductions</th>
                  {hasActionsColumn && <th scope="col" />}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="fw-medium py-3">
                      {payrollItemEmployeeLabel(item)}
                    </td>
                    <td className="py-3">
                      {item.status ? (
                        <StatusBadge value={item.status} map={payrollItemStatusMap} />
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-end tabular-nums py-3">
                      {item.employeeSalary != null
                        ? formatCurrency(item.employeeSalary)
                        : "—"}
                    </td>
                    <td className="text-end tabular-nums py-3">
                      {item.grossPay != null
                        ? formatCurrency(item.grossPay)
                        : "—"}
                    </td>
                    <td className="text-end tabular-nums fw-medium py-3">
                      {item.netPay != null ? formatCurrency(item.netPay) : "—"}
                    </td>
                    <td className="small py-3">
                      {item.earnings?.length ? (
                        <div className="d-flex flex-column gap-1">
                          {item.earnings.map((e, i) => (
                            <div key={i} className="d-flex justify-content-between gap-3">
                              <span className="text-muted">{e.label ?? e.type ?? "—"}</span>
                              <span className="tabular-nums text-body">{formatCurrency(e.amount)}</span>
                            </div>
                          ))}
                        </div>
                      ) : <span className="text-muted">—</span>}
                    </td>
                    <td className="small py-3">
                      {item.deductions?.length ? (
                        <div className="d-flex flex-column gap-1">
                          {item.deductions.map((d, i) => (
                            <div key={i} className="d-flex justify-content-between gap-3">
                              <span className="text-muted">{d.label ?? d.type ?? "—"}</span>
                              <span className="tabular-nums text-danger">-{formatCurrency(d.amount)}</span>
                            </div>
                          ))}
                        </div>
                      ) : <span className="text-muted">—</span>}
                    </td>
                    {hasActionsColumn && (
                      <td className="py-3">
                        <div className="d-flex gap-1">
                          {onAdjust && (
                            <Button
                              size="sm"
                              color="soft-secondary"
                              onClick={() => onAdjust(item)}
                              title="Manual adjustments"
                            >
                              <i className="ri-equalizer-line align-middle" />
                            </Button>
                          )}
                          {onViewSlip && (
                            <Button
                              size="sm"
                              color="soft-primary"
                              onClick={() => onViewSlip(item.id)}
                              title="View payslip"
                            >
                              <i className="ri-file-text-line align-middle" />
                            </Button>
                          )}
                          {onPay && canPayPayrollItem(item.status) && (
                            <Button
                              size="sm"
                              color="soft-success"
                              onClick={() => onPay(item)}
                              title="Pay individually (fallback — use Pay run for the normal flow)"
                            >
                              <i className="ri-hand-coin-line align-middle" />
                            </Button>
                          )}
                          {item.status === PayrollItemStatus.PAID &&
                            (Number(item.netPay ?? item.netAmount ?? 0) > 0 ? (
                              onViewReceipt && (
                                <Button
                                  size="sm"
                                  color="soft-success"
                                  onClick={() => onViewReceipt(item)}
                                  title="View receipt"
                                >
                                  <i className="ri-receipt-line align-middle" />
                                </Button>
                              )
                            ) : (
                              <span
                                className="text-muted small align-self-center"
                                title="This item nets to ₹0 — settled with no Payment record"
                              >
                                Settled — no disbursement
                              </span>
                            ))}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-light">
                <tr>
                  <th scope="row">Totals</th>
                  <td colSpan={2} />
                  <td className="text-end fw-semibold tabular-nums">
                    {formatCurrency(totals.gross)}
                  </td>
                  <td className="text-end fw-semibold text-success tabular-nums">
                    {formatCurrency(totals.net)}
                  </td>
                  <td colSpan={hasActionsColumn ? 3 : 2} />
                </tr>
              </tfoot>
            </Table>
          </div>
        ) : (
          <div className="text-center py-5 px-3">
            <div className="avatar-md mx-auto mb-3">
              <span className="avatar-title rounded-circle bg-light text-muted">
                <i className="ri-team-line fs-20" />
              </span>
            </div>
            <h6 className="mb-2">No employees in this run</h6>
            <p className="text-muted mb-0 mx-auto" style={{ maxWidth: 400 }}>
              Line items will appear here once payroll data is generated for
              this period. If this looks wrong, regenerate the run from the
              payroll list.
            </p>
            <Link
              to="/finance/payroll"
              className="btn btn-sm btn-soft-primary mt-3"
            >
              Go to payroll runs
            </Link>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default PayrollRunEmployeesInRun;
